<template>
  <div class="user-risk-monitor">
    <div class="operation-bar">
      <el-row :gutter="16" type="flex" align="middle">
        <el-col :span="6">
          <el-date-picker
            v-model="filter.date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            style="width: 100%"
          ></el-date-picker>
        </el-col>
        <el-col :span="4">
          <el-input-number v-model="filter.min_ip_count" :min="1" :max="99" controls-position="right" style="width: 100%" />
          <div class="input-tip">异常IP阈值</div>
        </el-col>
        <el-col :span="4">
          <el-input-number v-model="filter.min_direct_downloads" :min="1" :max="999" controls-position="right" style="width: 100%" />
          <div class="input-tip">直连阈值</div>
        </el-col>
        <el-col :span="4">
          <el-input-number v-model="filter.min_cloud_downloads" :min="1" :max="999" controls-position="right" style="width: 100%" />
          <div class="input-tip">网盘阈值</div>
        </el-col>
        <el-col :span="6" style="text-align:right;">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetFilter">重置</el-button>
        </el-col>
      </el-row>
    </div>

    <el-table :data="tableData" v-loading="loading" border @sort-change="handleSortChange">
      <el-table-column prop="user_id" label="用户ID" width="90" sortable="custom"></el-table-column>
      <el-table-column prop="name" label="用户名" min-width="160"></el-table-column>
      <el-table-column prop="daily_login_ip_count" label="当日登录IP数" width="130" sortable="custom">
        <template slot-scope="scope">
          <el-tag :type="scope.row.daily_login_ip_count >= threshold.minIpCount ? 'danger' : 'info'">
            {{ scope.row.daily_login_ip_count }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="moqu_direct_download_count" label="直连下载次数" width="130" sortable="custom">
        <template slot-scope="scope">
          <el-tag :type="scope.row.moqu_direct_download_count >= threshold.minDirectDownloads ? 'danger' : 'warning'">
            {{ scope.row.moqu_direct_download_count }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="cloud_download_count" label="网盘下载次数" width="130" sortable="custom">
        <template slot-scope="scope">
          <el-tag :type="scope.row.cloud_download_count >= threshold.minCloudDownloads ? 'danger' : 'success'">
            {{ scope.row.cloud_download_count }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="is_abnormal" label="系统判定" width="120" sortable="custom">
        <template slot-scope="scope">
          <el-tag :type="scope.row.is_abnormal ? 'danger' : 'success'">
            {{ scope.row.is_abnormal ? '异常' : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="人工标记" width="180">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_marked" type="warning">已标记</el-tag>
          <span v-else class="normal-text">未标记</span>
          <div v-if="scope.row.mark_note" class="note-text">{{ scope.row.mark_note }}</div>
        </template>
      </el-table-column>
      <el-table-column label="账号状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.is_banned ? 'danger' : 'success'">
            {{ scope.row.is_banned ? '已封禁' : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" width="330">
        <template slot-scope="scope">
          <el-button
            size="mini"
            type="primary"
            plain
            @click="openDetailDialog(scope.row)"
          >
            查看详情
          </el-button>
          <el-button
            size="mini"
            :type="scope.row.is_marked ? 'info' : 'warning'"
            @click="toggleMark(scope.row)"
          >
            {{ scope.row.is_marked ? '取消标记' : '标记异常' }}
          </el-button>
          <el-button
            size="mini"
            :type="scope.row.is_banned ? 'success' : 'danger'"
            @click="toggleBan(scope.row)"
          >
            {{ scope.row.is_banned ? '解封' : '封禁' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      >
      </el-pagination>
    </div>

    <el-dialog
      :title="`风控详情 - ${detailData.user.name || ''}`"
      :visible.sync="detailDialogVisible"
      width="1100px"
      class="risk-detail-dialog"
    >
      <div v-loading="detailLoading">
        <el-descriptions :column="4" border size="small" v-if="detailData.user.id">
          <el-descriptions-item label="用户ID">{{ detailData.user.id }}</el-descriptions-item>
          <el-descriptions-item label="用户名">{{ detailData.user.name }}</el-descriptions-item>
          <el-descriptions-item label="统计日期">{{ detailData.date || currentQueryDate }}</el-descriptions-item>
          <el-descriptions-item label="总活跃IP数">{{ detailData.summary.totalDistinctIps }}</el-descriptions-item>
          <el-descriptions-item label="登录IP数">{{ detailData.summary.loginDistinctIps }}</el-descriptions-item>
          <el-descriptions-item label="下载IP数">{{ detailData.summary.downloadDistinctIps }}</el-descriptions-item>
          <el-descriptions-item label="直连下载次数">{{ detailData.summary.moquDirectDownloadCount }}</el-descriptions-item>
          <el-descriptions-item label="网盘下载次数">{{ detailData.summary.cloudDownloadCount }}</el-descriptions-item>
        </el-descriptions>

        <div class="detail-section-title">IP明细（可用于截图举证）</div>
        <el-table :data="detailData.ipDetails" border max-height="260">
          <el-table-column prop="ip" label="IP地址" min-width="160"></el-table-column>
          <el-table-column prop="loginCount" label="登录次数" width="100"></el-table-column>
          <el-table-column prop="downloadCount" label="下载次数" width="100"></el-table-column>
          <el-table-column prop="moquDirectCount" label="直连次数" width="100"></el-table-column>
          <el-table-column prop="cloudCount" label="网盘次数" width="100"></el-table-column>
          <el-table-column prop="lastActivityTime" label="最后活跃时间" min-width="180">
            <template slot-scope="scope">
              {{ formatTime(scope.row.lastActivityTime) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="detail-section-title">直连下载明细</div>
        <el-table :data="detailData.moquDirectDownloads" border max-height="220">
          <el-table-column prop="created_at" label="时间" width="170">
            <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
          </el-table-column>
          <el-table-column prop="ip_address" label="IP" width="160"></el-table-column>
          <el-table-column prop="game_title" label="游戏" min-width="220"></el-table-column>
          <el-table-column prop="resource_id" label="资源ID" width="90"></el-table-column>
        </el-table>

        <div class="detail-section-title">网盘下载明细</div>
        <el-table :data="detailData.cloudDownloads" border max-height="220">
          <el-table-column prop="created_at" label="时间" width="170">
            <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
          </el-table-column>
          <el-table-column prop="ip_address" label="IP" width="160"></el-table-column>
          <el-table-column prop="game_title" label="游戏" min-width="220"></el-table-column>
          <el-table-column prop="resource_id" label="资源ID" width="90"></el-table-column>
        </el-table>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDailyRiskStats, markDailyRiskUser, setUserBanStatus, getUserDailyRiskDetail } from '@/api'

export default {
  name: 'UserRiskMonitor',
  data() {
    return {
      loading: false,
      tableData: [],
      filter: {
        date: '',
        min_ip_count: 3,
        min_direct_downloads: 10,
        min_cloud_downloads: 20
      },
      sort: {
        sort_by: 'is_abnormal',
        sort_order: 'desc'
      },
      threshold: {
        minIpCount: 3,
        minDirectDownloads: 10,
        minCloudDownloads: 20
      },
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      detailDialogVisible: false,
      detailLoading: false,
      detailData: {
        date: '',
        user: {},
        summary: {
          totalDistinctIps: 0,
          loginDistinctIps: 0,
          downloadDistinctIps: 0,
          moquDirectDownloadCount: 0,
          cloudDownloadCount: 0,
          totalDownloadCount: 0
        },
        ipDetails: [],
        moquDirectDownloads: [],
        cloudDownloads: []
      }
    }
  },
  computed: {
    currentQueryDate() {
      return this.filter.date || new Date().toISOString().slice(0, 10)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    handleSearch() {
      this.pagination.currentPage = 1
      this.loadData()
    },

    handleSortChange({ prop, order }) {
      if (!prop || !order) {
        this.sort = {
          sort_by: 'is_abnormal',
          sort_order: 'desc'
        }
      } else {
        this.sort = {
          sort_by: prop,
          sort_order: order === 'ascending' ? 'asc' : 'desc'
        }
      }
      this.pagination.currentPage = 1
      this.loadData()
    },

    async loadData() {
      this.loading = true
      try {
        const params = {
          ...this.filter,
          ...this.sort,
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize
        }
        const res = await getDailyRiskStats(params)
        if (res.success) {
          this.tableData = res.data.data || []
          this.pagination.total = res.data.pagination?.totalItems || 0
          this.threshold = {
            minIpCount: res.data.threshold?.minIpCount || this.filter.min_ip_count,
            minDirectDownloads: res.data.threshold?.minDirectDownloads || this.filter.min_direct_downloads,
            minCloudDownloads: res.data.threshold?.minCloudDownloads || this.filter.min_cloud_downloads
          }
        } else {
          this.$message.error(res.message || '获取风控统计失败')
        }
      } catch (err) {
        console.error('获取风控统计失败:', err)
        this.$message.error(err.response?.data?.message || '获取风控统计失败')
      } finally {
        this.loading = false
      }
    },

    resetFilter() {
      this.filter = {
        date: '',
        min_ip_count: 3,
        min_direct_downloads: 10,
        min_cloud_downloads: 20
      }
      this.sort = {
        sort_by: 'is_abnormal',
        sort_order: 'desc'
      }
      this.pagination.currentPage = 1
      this.loadData()
    },

    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.currentPage = 1
      this.loadData()
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadData()
    },

    async openDetailDialog(row) {
      this.detailDialogVisible = true
      this.detailLoading = true
      this.detailData = {
        date: '',
        user: { id: row.user_id, name: row.name },
        summary: {
          totalDistinctIps: 0,
          loginDistinctIps: 0,
          downloadDistinctIps: 0,
          moquDirectDownloadCount: 0,
          cloudDownloadCount: 0,
          totalDownloadCount: 0
        },
        ipDetails: [],
        moquDirectDownloads: [],
        cloudDownloads: []
      }

      try {
        const res = await getUserDailyRiskDetail(row.user_id, {
          date: this.currentQueryDate
        })
        if (res.success) {
          this.detailData = {
            date: res.data.date,
            user: res.data.user || {},
            summary: res.data.summary || {},
            ipDetails: res.data.ipDetails || [],
            moquDirectDownloads: res.data.moquDirectDownloads || [],
            cloudDownloads: res.data.cloudDownloads || []
          }
        } else {
          this.$message.error(res.message || '获取风控详情失败')
        }
      } catch (err) {
        console.error('获取风控详情失败:', err)
        this.$message.error(err.response?.data?.message || '获取风控详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    toggleMark(row) {
      const targetStatus = row.is_marked ? 0 : 1
      const actionText = targetStatus ? '标记异常' : '取消标记'

      this.$prompt(`${actionText}备注`, actionText, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: row.mark_note || '',
        inputPlaceholder: '可选，建议记录异常原因'
      }).then(async ({ value }) => {
        try {
          const riskDate = this.filter.date || new Date().toISOString().slice(0, 10)
          await markDailyRiskUser(row.user_id, {
            risk_date: riskDate,
            is_marked: targetStatus,
            mark_note: value || ''
          })
          this.$message.success(`${actionText}成功`)
          this.loadData()
        } catch (err) {
          console.error(`${actionText}失败:`, err)
          this.$message.error(err.response?.data?.message || `${actionText}失败`)
        }
      }).catch(() => {})
    },

    toggleBan(row) {
      const targetStatus = row.is_banned ? 0 : 1
      const actionText = targetStatus ? '封禁' : '解封'

      this.$prompt(`${actionText}原因`, actionText, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: row.ban_reason || '',
        inputPlaceholder: targetStatus ? '请输入封禁原因' : '可选'
      }).then(async ({ value }) => {
        try {
          await setUserBanStatus(row.user_id, {
            is_banned: targetStatus,
            ban_reason: value || ''
          })
          this.$message.success(`${actionText}成功`)
          this.loadData()
        } catch (err) {
          console.error(`${actionText}失败:`, err)
          this.$message.error(err.response?.data?.message || `${actionText}失败`)
        }
      }).catch(() => {})
    },

    formatTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      const second = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    }
  }
}
</script>

<style scoped>
.user-risk-monitor {
  padding: 20px;
}

.operation-bar {
  margin-bottom: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  text-align: right;
}

.input-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.note-text {
  margin-top: 4px;
  color: #606266;
  font-size: 12px;
  word-break: break-all;
}

.normal-text {
  color: #909399;
  font-size: 12px;
}

.detail-section-title {
  margin: 16px 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.risk-detail-dialog :deep(.el-dialog__body) {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
